import React, { useState, useEffect } from 'react'
import { Button } from '@apex/component-library'
import { Box, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { DistributionReasonInfo, RetirementConstraintsResponse, SearchAccountsResponsePayload } from '@Types'
import { CountryItem } from '../../reducers/types'
import { useFormikContext } from 'formik'
import { CheckTransferValues, DeliveryMethodTypes } from '../../containers/CheckTransfers/Types'
import TransferFrequency from '../TransferFrequency'
import CheckTransferPreviewModal from '../CheckTransferPreviewModal'
import { MAX_FORM_WIDTH } from '../../utils'
import { PREVIOUS } from '../../constants'
import TransferFees from '../TransferFees'
import { FeeTransferType, TransferFeeDescriptionType } from '../TransferFees/types'
import CheckTransferDetails from './CheckTransferDetails'
import { FromAccount } from '../FromAccount'

export interface Props {
  fromAccount: string
  iraConstraintData: RetirementConstraintsResponse
  account: SearchAccountsResponsePayload
  amountAvailable: number
  getCountryList: () => void
  countryList: CountryItem[]
  countryListLoading: boolean
  countryListFailedMessage?: string
  countryAutoSelectDataSet: { value: string }[]
  hasOvernightToApexPerm: boolean
  hasSubmitPermission: boolean
  selectedReason?: DistributionReasonInfo | null
  handleSelectReason: (reason: DistributionReasonInfo | null) => void
}

const CheckTransferForm = ({
  fromAccount,
  iraConstraintData,
  account,
  amountAvailable,
  getCountryList,
  countryList,
  countryListLoading,
  countryListFailedMessage,
  countryAutoSelectDataSet,
  hasOvernightToApexPerm,
  hasSubmitPermission,
  handleSelectReason,
  selectedReason
}: Props) => {
  const formik = useFormikContext<CheckTransferValues>()
  const { values, isValid, errors } = formik
  const [hasTotalDistributionError, setHasTotalDistributionError] = useState(false)
  const [applicableFees, setApplicableFees] = useState<TransferFeeDescriptionType[]>([])

  const { isOpen, onOpen, onClose } = useDisclosure()

  const getApplicableFee = (
    country: string,
    deliveryMethod: DeliveryMethodTypes
  ): TransferFeeDescriptionType | undefined => {
    const usCountryNames = ['US', 'USA', 'United States']
    if (usCountryNames.includes(country)) {
      switch (deliveryMethod) {
        case 'STANDARD':
          return 'CHECK_REGULAR_DELIVERY'
        case 'OVERNIGHT':
          return 'CHECK_OVERNIGHT_DELIVERY'
        case 'OVERNIGHT_TO_APEX':
          return 'CHECK_OVERNIGHT_TO_BROKER'
      }
    } else {
      switch (deliveryMethod) {
        case 'STANDARD':
          return 'CHECK_INTERNATIONAL_REGULAR_DELIVERY'
        case 'OVERNIGHT':
          return 'CHECK_INTERNATIONAL_OVERNIGHT_DELIVERY'
        case 'OVERNIGHT_TO_APEX':
          return 'CHECK_OVERNIGHT_TO_BROKER'
      }
    }
  }

  useEffect(() => {
    let applicableFees: TransferFeeDescriptionType[] = []
    let deliveryFee: TransferFeeDescriptionType | undefined
    if (values.sendTo === 'ACCOUNT-HOLDER' && account.accountAddress?.country) {
      deliveryFee = getApplicableFee(account.accountAddress.country, values.deliveryMethod)
    } else if (!errors.recipientCountry && values.recipientCountry) {
      deliveryFee = getApplicableFee(values.recipientCountry, values.deliveryMethod)
    }
    if (!!deliveryFee) {
      applicableFees.push(deliveryFee)
    }
    if (iraConstraintData.retirementAccount && values.totalBalanceCheckbox) {
      applicableFees.push('CLOSING')
    }
    setApplicableFees(applicableFees as TransferFeeDescriptionType[])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    account.accountAddress,
    values.sendTo,
    // only check for fees if there is a change in valid/invalid recipient country
    // checking every time values.recipientCountry changes makes the input too slow
    errors.recipientCountry,
    values.deliveryMethod,
    iraConstraintData.retirementAccount,
    values.totalBalanceCheckbox
  ])

  return (
    <Box data-testid='check-transfer-form'>
      <Box width='100%' maxW={MAX_FORM_WIDTH} marginTop={6} marginBottom={6}>
        <FromAccount displayedValue={fromAccount} />
      </Box>
      <Box as='form' pb={4} maxW={MAX_FORM_WIDTH} w='100%'>
        <Stack spacing={6}>
          <CheckTransferDetails
            account={account}
            amountAvailable={amountAvailable}
            constraintData={iraConstraintData}
            countryList={countryList}
            getCountryList={getCountryList}
            countryListLoading={countryListLoading}
            countryListFailedMessage={countryListFailedMessage}
            countryAutoSelectDataSet={countryAutoSelectDataSet}
            hasOvernightToApexPerm={hasOvernightToApexPerm}
            hasTotalDistributionError={hasTotalDistributionError}
            setHasTotalDistributionError={setHasTotalDistributionError}
            handleSelectReason={handleSelectReason}
            selectedReason={selectedReason}
          />
          <TransferFrequency
            selectedRecurrence={values.transferRecurrence}
            selectedFrequency={values.transferFrequency}
            selectedWhenToSend={values.whenToSend}
            selectedScheduledDate={values.transferScheduledDate}
            selectedStartDate={values.transferStartDate}
            numberOfTransfers={values.numberOfTransfers}
            isTotalBalanceTransfer={values.totalBalanceCheckbox}
            isPreviousYearContribution={values.contributionYear === PREVIOUS}
          />
          <TransferFees
            accountNumber={account.accountNumber}
            feeTransferType={FeeTransferType.checkWithdrawal}
            applicableFees={applicableFees}
          />
          <Box>
            <Text as='h6'>
              Checks are submitted immediately, but not processed on weekends or banking holidays. Check processing is
              performed in a single daily batch and sent to the bank each morning for printing.
            </Text>
          </Box>
          <Box paddingTop={'1.5em'}>
            <Button
              variant='primary'
              data-testid='checks-next-btn'
              isDisabled={!isValid || hasTotalDistributionError}
              onClick={onOpen}
            >
              Next
            </Button>
          </Box>
          <CheckTransferPreviewModal
            account={account}
            isOpen={isOpen}
            onClose={onClose}
            fromAccount={fromAccount}
            selectedReason={selectedReason}
            setSelectedReason={handleSelectReason}
            hasSubmitPermission={hasSubmitPermission}
            countryList={countryList}
            amountAvailable={amountAvailable}
          />
        </Stack>
      </Box>
    </Box>
  )
}

export default CheckTransferForm
